import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    HostListener,
    inject,
    input,
    model,
    output,
    signal,
    TemplateRef,
    untracked,
    viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';
import { AppContext } from 'src/app/app/context';
import { VisualDataSource } from 'src/app/pbir/visuals/data';
import { Visual } from 'src/app/pbir/visuals/Visual';
import { VisualField } from 'src/app/pbir/visuals/VisualField';
import { VisualFieldTarget } from 'src/app/pbir/visuals/VisualFieldTarget';
import { VisualFilter } from 'src/app/pbir/visuals/VisualFilter';
import { VisualHostComponent } from 'src/app/pbir/visuals/VisualHost.component';
import { SortedChartTypeList } from 'src/app/shared/chart/charts';
import { TrgDragDropService } from '../../../pbir/utils/TrgDragDrop';
import { TrgDrop } from '../../../pbir/utils/TrgDrop.directive';
import { VisualHost } from '../../../pbir/visuals/VisualHost';
import { DataQueryService, GetQueryPlanResponse, UserRole } from '../../../services/api.services';
import { DialogsService } from '../../../services/dialogs.service';
import { GenericAlertButton } from '../../../shared/dialogs';
import { catchAppError } from '../../../shared/utils';
import { FilterEditor } from './filters/FilterEditor.component';
import { VisualEditorField, VisualEditorFieldComponent } from './layout/Field.component';
import { validVisualSelectClasses, VisualEditor, VisualEditorComponent } from './VisualEditor.component';

@Component({
  selector: 'visual-editor-canvas-section',
  templateUrl: './CanvasSection.component.html',
  styleUrls: ['./CanvasSection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
        '[class.filters-collapsed]': 'filtersCollapsed()'
    },
  standalone: false
})
export class VisualEditorCanvasComponent {
  readonly FieldDisplay = VisualEditorField.Display;

  readonly destroyRef = inject(DestroyRef);
  readonly appContext = inject(AppContext);
  readonly dragDrop = inject(TrgDragDropService);
  readonly visualEditor = inject(VisualEditorComponent);
  readonly dataQueryService = inject(DataQueryService);
  readonly dialogs = inject(DialogsService);
  
  readonly visualHost = viewChild<VisualHostComponent>('visualHost');
  readonly busy = input(false);
  
  readonly refreshPending = input(false);
  readonly refreshVisual = output();

  readonly chartTypes = SortedChartTypeList;

  readonly currencies = this.visualEditor.currencies;
  readonly currenciesLoading = this.visualEditor.currenciesLoading;
  readonly currencySelection = computed(() => !!this.visualEditor.dataset()?.schema().supportsCurrencyConversion);

  readonly allowLocaleSelection = computed(() => !!this.appContext.features()?.uI2.localeDropdown && this.appContext.user().role >= UserRole.InsightsAdmin);
  readonly locales = computed(() => this.appContext.locales());
  readonly selectedLocale = this.visualEditor.visualLocale;

  readonly userDatasetFailure = computed(() =>
    this.visualEditor.isUserDataset() && this.visualEditor.userDatasetState().status == VisualEditor.UserDatasetStatus.Failed
  );

  setSelectedLocale(localeId: string) {
    localeId = localeId?.toLowerCase();

    this.selectedLocale.set(
      this.appContext.locales().find(x => x.id.toLowerCase() == localeId));
  }


  readonly filtersCollapsedPrefKey = `VisualEditor.filtersCollapsed`;
  readonly filtersCollapsed = model(this.visualEditor.userPrefs.get(this.filtersCollapsedPrefKey, true));
  setFiltersCollapsed(value: boolean) {
    this.filtersCollapsed.set(value);
    this.visualEditor.userPrefs.set(this.filtersCollapsedPrefKey, value);
  }

  readonly droppingTransition = signal(false);
  readonly dropping = computed(() => this.dragDrop.drag() || this.droppingTransition());

  readonly visualId = input<number>(0);
  readonly visual = input.required<Visual>();
  readonly visualDataSource = input.required<VisualDataSource>();
  readonly fieldsEmpty = computed(() => this.visual()?.fields?.isEmpty() !== false);

  readonly readyToRender = computed(() => {
    let v = this.visual();
    if (!v)
      return false;

    return v.isValid();
  });

  readonly hideVisualHost = computed(() => this.visualEditor.userDatasetId() && !this.visualEditor.userDatasetLoaded());


  readonly needsHostLoadingOverlay = computed(() => {
    let v = this.visual();
    return !v?.type.loadedWhenRendering;
  });

  readonly visualRendered = computed(() => {
    let [v, h] = [this.visual(), this.visualHost()];

    if (!v || !h)
      return false;

    let valid = v?.isValid();
    if (!valid)
      return false;

    let rs = h?.renderState();
    if (rs?.rendered && !rs?.rendering)
      return true;

    return false;
  });

  readonly rowCountAvailable = computed(() => !!this.visual()?.isValid());
  readonly rowCount = computed(() => (this.visualHost()?.rowCount() ?? { loaded: false }));
  readonly showRowCount = computed(() => !!this.visual()?.type?.showRowCount);

  readonly filterEditorGroups = computed(() => {
    let groups = this.visualEditor.dataset()?.groups() ?? [];
    return groups.map(g => {
      let group = new FilterEditor.Group(g.shortTitle());

      for (let i of g.children()) {
        let item = new FilterEditor.Item(
          group,
          i.target,
          i.shortTitle,
          i.longTitle,
          i.icon,
          !!i.children().length,
          null);

        group.items.push(item);

        for (let c of i.children()) {
          let child = new FilterEditor.Item(
            group,
            c.target,
            c.shortTitle,
            c.longTitle,
            c.icon,
            false,
            item);

          group.items.push(child);
        }
      }

      return group;
    });
  });


  readonly filters = computed(() => this.visual()?.filters() ?? []);

  filtersChange(filters: VisualFilter[]) {
    this.visual()?.filters.set(filters);
  }


  onFilterDropped(e: TrgDrop.DropEvent, filter?: VisualFilter, index?: number) {
    let preview = e.preview;
    let rect = e.drop.element.getBoundingClientRect();

    let handleDrop = () => {
      if (e.data instanceof VisualEditorFieldComponent) {
        let dropData = e.data.dropData();
        let field;

        if (dropData instanceof VisualField)
          field = dropData;
        else if (dropData instanceof VisualFieldTarget)
          field = new VisualField(dropData);
        else if (dropData instanceof VisualEditor.DatasetItem)
          field = new VisualField(dropData.target());

        this.visualEditor.addFilterFromField(field, { index, userAction: true });
        this.filtersCollapsed.set(false);
      }
      else if (e.data instanceof VisualFilter) {
        this.visualEditor.placeFilter(e.data, { index, userAction: true });
        this.filtersCollapsed.set(false);
      }
    };

    this.droppingTransition.set(true);

    preview.transitionTo({
      x: Math.ceil(rect.x),
      y: Math.ceil(rect.y),
      maxWaitTime: 300,
      destoryAfter: false,
      duration: 200
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => {
        try {
          handleDrop();
        }
        finally {
          this.droppingTransition.set(false);

          preview.destroy();
        }
      })
    ).subscribe();
  }

  onFilterDropEnter(e: TrgDrop.DropEvent) {
  }

  onFilterDropExit(e: TrgDrop.DropEvent) {
  }


  onVisualHostStateChange() {
    this.visualEditor.onVisualChange({ userAction: false });
  }


  private clearSelection() {
    this.visualHost()?.selectionClear(VisualHost.EventTrigger.External);
  }

  onBeforeVisualEvent(e: VisualHost.Event) {
    if (e.type == VisualHost.EventType.ClickEarlyEvent) {
      if (e.resetCrossFilter) {
        this.clearSelection();
      }
    }
  }
  
  @HostListener('document:click', ['$event'])
  @HostListener('document:contextmenu', ['$event'])
  onDocumentClick(e: MouseEvent) {
    if (e.button === 0) {
      let target = e.target as HTMLElement;

      if (![...validVisualSelectClasses,'.visual-editor-canvas-section-visual'].some(x => target.closest(x))) {
        this.clearSelection();
      }
    }
  }


  readonly canViewQueryPlan = computed(() =>
    this.appContext.user()?.isSuperAdmin &&
    !!this.appContext.features()?.dataQuery?.recordPlansForFrontendQueries);

  readonly queryId = computed(() => this.visualHost()?.component()?.getDataQueryId());
  readonly queryPlanLoaded = signal(false);
  readonly queryPlanLoading = signal(false);

  readonly queryPlanResponse = signal<GetQueryPlanResponse>(null);
  readonly queryPlan = computed(() => this.queryPlanResponse()?.plan);
  readonly queryPlanQueryIdChange = effect(() => {
    if (!this.queryPlanResponse())
      return;

    if (this.queryId() != this.queryPlanResponse()?.queryId) {
      untracked(() => {
        this.queryPlanLoaded.set(false);
        this.queryPlanLoading.set(false);
        this.queryPlanResponse.set(null);
      });
    }
  });

  readonly queryPlanViewTemplate = viewChild<TemplateRef<any>>('planTemplate');

  loadQueryPlanClick() {
    if (!this.queryId())
      return;

    this.queryPlanLoading.set(true);
    this.queryPlanLoaded.set(false);

    this.dataQueryService.getQueryPlan({
      queryId: this.queryId()
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(p => {
        this.queryPlanResponse.set(p);
        this.queryPlanLoaded.set(true);
        this.queryPlanLoading.set(false);
      }),
      catchAppError(err => {
        this.queryPlanLoaded.set(false);
        this.queryPlanLoading.set(false);
      }, { errorMessage: 'Failed to load query plan definition' })
    ).subscribe();
  }

  viewQueryPlanClick() {
    if (!this.queryPlan())
      return;

    this.dialogs.openAlert({
      buttons: [GenericAlertButton.close],
      content: this.queryPlanViewTemplate(),
      showClose: true,
      title: 'Query Plan',
    }, { width: '1200px', maxWidth: '100vw' });
  }

}

export namespace VisualEditorCanvas {
  export interface CurrencyModel {
    code: string;
    title: string;
  }
}
